import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { AppState, type AppStateStatus } from 'react-native';

import { useAuth } from './AuthContext';
import {
  loadHouseholdViewMode,
  saveHouseholdViewMode,
} from '../core/storage/householdViewStorage';
import {
  getHousehold,
  type HouseholdResponse,
  type HouseholdViewMode,
} from '../services/householdService';

type HouseholdMembershipContextValue = {
  view: HouseholdViewMode;
  /**
   * False until AsyncStorage view preference has been read.
   * Hooks should wait so we don't fetch as `combined` then immediately again as `personal`.
   */
  viewReady: boolean;
  setView: (view: HouseholdViewMode) => void;
  household: HouseholdResponse | null;
  loading: boolean;
  refresh: () => Promise<void>;
  /** Apply accept/GET payload immediately (don't wait for another fetch). */
  applyHousehold: (household: HouseholdResponse) => void;
  canInvite: boolean;
  memberCount: number;
};

const HouseholdMembershipContext = createContext<HouseholdMembershipContextValue | null>(
  null,
);

export function HouseholdMembershipProvider({ children }: PropsWithChildren) {
  const { isAuthenticated, user } = useAuth();
  const [view, setViewState] = useState<HouseholdViewMode>('combined');
  const [viewReady, setViewReady] = useState(false);
  const [household, setHousehold] = useState<HouseholdResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void loadHouseholdViewMode()
      .then((stored) => {
        if (cancelled) return;
        if (stored) setViewState(stored);
      })
      .finally(() => {
        if (!cancelled) setViewReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setView = useCallback((next: HouseholdViewMode) => {
    setViewState(next);
    void saveHouseholdViewMode(next);
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setHousehold(null);
      return;
    }
    setLoading(true);
    try {
      const data = await getHousehold();
      setHousehold(data);
    } catch {
      setHousehold(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const applyHousehold = useCallback((next: HouseholdResponse) => {
    setHousehold(next);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh, user?.householdId, user?.plan]);

  // Partner may join while this tab is open — re-check on foreground.
  useEffect(() => {
    const onChange = (status: AppStateStatus) => {
      if (status === 'active' && isAuthenticated) {
        void refresh();
      }
    };
    const sub = AppState.addEventListener('change', onChange);
    return () => sub.remove();
  }, [isAuthenticated, refresh]);

  const memberCount = household?.memberCount ?? household?.members.length ?? 1;
  const freeCap = household?.freeMemberCap ?? 2;
  const maxMembers = household?.maxMembers ?? freeCap;
  const hasPro = Boolean(household?.hasPro || user?.plan === 'pro');
  /**
   * Free: invite until couple (2).
   * Pro: invite more (group) until maxMembers.
   */
  const canInvite =
    typeof household?.canInvite === 'boolean'
      ? household.canInvite
      : Boolean(
          household?.myRole &&
            memberCount < maxMembers &&
            (memberCount < freeCap || hasPro),
        );

  const value = useMemo<HouseholdMembershipContextValue>(
    () => ({
      view,
      viewReady,
      setView,
      household,
      loading,
      refresh,
      applyHousehold,
      canInvite,
      memberCount,
    }),
    [
      view,
      viewReady,
      setView,
      household,
      loading,
      refresh,
      applyHousehold,
      canInvite,
      memberCount,
    ],
  );

  return (
    <HouseholdMembershipContext.Provider value={value}>
      {children}
    </HouseholdMembershipContext.Provider>
  );
}

export function useHouseholdMembership(): HouseholdMembershipContextValue {
  const ctx = useContext(HouseholdMembershipContext);
  if (!ctx) {
    throw new Error('useHouseholdMembership must be used within HouseholdMembershipProvider');
  }
  return ctx;
}
